<template>
  <div
    class="irp-recent-activity-event"
    data-testid="irp-recent-activity-event"
  >
    <div class="irp-recent-activity-event__content">
      <div class="irp-recent-activity-event__meta">
        <div class="irp-recent-activity-event__actor">
          <BittsAvatar
            v-bind="avatarProps"
            size="x-small"
            :show-initials="true"
            :show-border="true"
          />
          <span class="irp-recent-activity-event__actor-name">
            {{ actorName }}
          </span>
        </div>
        <span class="irp-recent-activity-event__timestamp">
          {{ timestamp }}
        </span>
      </div>
      <p v-if="noteText" class="irp-recent-activity-event__body">
        {{ noteText }}
      </p>
      <AttributionTimelineDynamicContent
        v-else
        class="irp-recent-activity-event__body"
        :activity="event"
        :account-name="accountName"
      />
    </div>
    <RouterLink v-if="listId" :to="`/collaborate/${listId}`">
      <BittsButton
        text="View list"
        type="neutral"
        variant="ghost"
        size="x-small"
        data-testid="irp-recent-activity-event-list-link"
      />
    </RouterLink>
  </div>
</template>

<script setup lang="ts">
import { BittsAvatar, BittsButton } from '@crossbeam/bitts';

import { DateTime } from 'luxon';
import { computed } from 'vue';

import AttributionTimelineDynamicContent from '@/components/attribution/AttributionTimelineDynamicContent.vue';

import useAuth from '@/composables/useAuth';
import { timeSince } from '@/date_time_utils';
import { usePartnersStore, useTeamStore } from '@/stores';
import type { TimelineEvent } from '@/types/timeline';

type Props = {
  event: TimelineEvent;
  accountName?: string;
};

const { event, accountName = '' } = defineProps<Props>();

const RECENT_HOURS = 24;

const teamStore = useTeamStore();
const partnersStore = usePartnersStore();
const { currentOrg } = useAuth();

const actionOrganizationUuid = computed(() =>
  'action_organization_uuid' in event.data
    ? event.data.action_organization_uuid
    : null,
);

const userId = computed(() =>
  'user_id' in event.data ? event.data.user_id : null,
);

const partner = computed(() =>
  partnersStore.getPartnerOrgByUuid(event.partner_organization_uuid ?? ''),
);

const actorUser = computed(() => {
  if (!userId.value) return null;
  const isOtherOrg =
    !!actionOrganizationUuid.value &&
    actionOrganizationUuid.value !== currentOrg.value.uuid;
  if (isOtherOrg) return null;
  return teamStore.getUserById(userId.value);
});

const actorName = computed(() => {
  if (actorUser.value) {
    const { first_name, last_name } = actorUser.value;
    return [first_name, last_name].filter(Boolean).join(' ');
  }
  return partner.value?.name ?? currentOrg.value.name;
});

const avatarProps = computed(() =>
  actorUser.value
    ? { user: actorUser.value }
    : { org: partner.value ?? currentOrg.value },
);

const noteText = computed(() =>
  'note_text' in event.data ? event.data.note_text : null,
);

const listId = computed(() =>
  'list_id' in event.data ? event.data.list_id : null,
);

const timestamp = computed(() => {
  const triggeredAt = DateTime.fromISO(event.triggered_at);
  const isRecent = Math.abs(triggeredAt.diffNow('hours').hours) < RECENT_HOURS;
  const time = isRecent
    ? timeSince(triggeredAt)
    : triggeredAt.toFormat('LLL dd, yyyy');
  return event.type === 'list_note_updated' ? `Updated ${time}` : time;
});
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-recent-activity-event {
  @apply flex items-start justify-between gap-8;
}

.irp-recent-activity-event__content {
  @apply flex flex-col gap-4 min-w-0;
}

.irp-recent-activity-event__meta {
  @apply flex items-center gap-8;
}

.irp-recent-activity-event__actor {
  @apply flex items-center gap-4 min-w-0;
}

.irp-recent-activity-event__actor-name {
  @apply text-sm font-bold text-neutral-text truncate;
}

.irp-recent-activity-event__timestamp {
  @apply text-sm text-neutral-text-weak whitespace-nowrap;
}

.irp-recent-activity-event__body {
  @apply text-sm text-neutral-text-strong;
}

.irp-recent-activity-event :deep(.timeline-event__dot) {
  @apply hidden;
}
</style>
